import React, { useState } from 'react';
import { useAuth } from '../components/auth/AuthContext';

const MIN_LENGTH = 8;

const Account = () => {
  const { user, changePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!current || !next || !confirm) {
      setError('All fields are required.');
      return;
    }
    if (next.length < MIN_LENGTH) {
      setError(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (next === current) {
      setError('New password must differ from the current password.');
      return;
    }

    setSubmitting(true);
    const result = await changePassword(current, next);
    setSubmitting(false);

    if (result.success) {
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="max-w-xl mx-auto mt-10 p-6">
      <h1 className="text-2xl font-semibold text-gray-100 mb-2">Account</h1>
      <div className="text-sm text-gray-400 mb-6">
        Signed in as <span className="text-gray-200">{user?.username}</span>
        {user?.role ? <span className="text-gray-500"> ({user.role})</span> : null}
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-100 mb-1">Change password</h2>
        <p className="text-sm text-gray-500 mb-5">
          Changing your password will sign out any other devices where you are
          logged in. This tab will stay signed in.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="current" className="block text-sm font-medium text-gray-300 mb-1">
              Current password
            </label>
            <input
              id="current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="next" className="block text-sm font-medium text-gray-300 mb-1">
              New password
            </label>
            <input
              id="next"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={submitting}
            />
            <div className="text-xs text-gray-500 mt-1">
              At least {MIN_LENGTH} characters. Must differ from your current password.
            </div>
          </div>

          <div>
            <label htmlFor="confirm" className="block text-sm font-medium text-gray-300 mb-1">
              Confirm new password
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-gray-100 focus:outline-none focus:border-blue-500"
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="bg-red-900/40 border border-red-800 text-red-200 text-sm rounded px-3 py-2">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-900/40 border border-green-800 text-green-200 text-sm rounded px-3 py-2">
              Password updated. Other devices have been signed out.
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-400 text-white font-medium rounded px-4 py-2 transition-colors"
          >
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Account;
